import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Auth } from '../../../../core/auth/auth';
import { CommonService } from '../../../../core/services/common';
import { Logger } from '../../../../core/services/logger';
import { Skeleton } from '../../../../shared/components/skeleton/skeleton';
import {
  CompanySwitchRequest,
  SWITCHABLE_ROLE_TYPES,
  SwitchableCompany,
  SwitchableCompanyView,
} from '../../../../models/company-switcher.model';

/**
 * Header company switcher.
 *
 * Lists the companies the signed-in user may act on and emits the one they pick.
 * It deliberately does NOT perform the switch — the parent owns that, so this
 * component stays presentational and the switch flow lives in one place.
 */
@Component({
  selector: 'app-company-switcher',
  standalone: true,
  imports: [CommonModule, FormsModule, Skeleton],
  templateUrl: './company-switcher.html',
  styleUrl: './company-switcher.scss',
})
export class CompanySwitcher implements OnInit, OnDestroy {
  private readonly auth = inject(Auth);
  private readonly commonService = inject(CommonService);
  private readonly logger = inject(Logger);
  private readonly elementRef = inject(ElementRef);

  /** Emitted when the user picks a company. The parent raises the confirm modal. */
  readonly companySelected = output<CompanySwitchRequest>();
  /** Emitted when the panel opens, so sibling header dropdowns can close. */
  readonly opened = output<void>();

  readonly companies = signal<SwitchableCompanyView[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly hasError = signal<boolean>(false);
  readonly isOpen = signal<boolean>(false);
  readonly searchTerm = signal<string>('');

  /** Object URLs created for logos, revoked on destroy to avoid leaking blobs. */
  private readonly objectUrls: string[] = [];

  /**
   * Whether the control is interactive.
   *
   * Two separate rules, deliberately kept apart:
   *  - only roles in `SWITCHABLE_ROLE_TYPES` (sub_admin, super_user) ever
   *    get a switcher — `admin` picks a company per record
   *    via an inline form field instead (it owns multiple companies with no
   *    single "active" one), and `employee` has exactly one company.
   *  - within that set, visibility is also driven by the resolved company
   *    count, so a sub_admin assigned to only one company sees a plain label
   *    rather than a dropdown that can only pick itself.
   *
   * Role NUMBERS are never used here — the resolved list is the source of truth,
   * which is what keeps this in step with the server's own access resolution.
   *
   * Bug 8724: `companies()` is also what `loadCompanies()` empties out on a
   * failed request (e.g. the current company/group has no menu access to the
   * list endpoint at all after a switch) - collapsing straight to the static,
   * unclickable label the moment that count hit <=1 meant a failed load
   * looked identical to "only one company", permanently hiding the one
   * control that could get the user out of a company with no accessible
   * pages. Stay interactive while loading or errored so the panel can still
   * open (to show its own loading/retry state) - only fall back to the inert
   * label once a load has actually *succeeded* with <=1 company.
   */
  readonly canSwitch = computed<boolean>(() => {
    const roleType = this.auth.currentUser()?.role_type;
    if (!roleType || !SWITCHABLE_ROLE_TYPES.includes(roleType)) return false;
    if (this.isLoading() || this.hasError()) return true;
    return this.companies().length > 1;
  });

  /** Whether to render anything at all. */
  readonly isVisible = computed<boolean>(() => {
    const roleType = this.auth.currentUser()?.role_type;
    return !!roleType && SWITCHABLE_ROLE_TYPES.includes(roleType);
  });

  /**
   * Active company id, with the same fallback chain the legacy sub-sidebar
   * switcher used: session `companyID` first, then the last company the user
   * switched to, then simply the first row in the list.
   *
   * The fallback matters because a primary Admin's `company_id` comes back as
   * an EMPTY array at login, before they have ever switched — reading only
   * `currentUser().companyID` left this at `null`, which blanked the trigger
   * name, left no row ticked, and collapsed the confirm modal's "from" card to
   * just a placeholder letter with no name or location (shorter than the
   * populated "to" card).
   */
  readonly activeCompanyId = computed<number | null>(() => {
    const fromSession = this.normalizeCompanyId(this.auth.currentUser()?.companyID);
    if (fromSession != null) return fromSession;

    const companies = this.companies();
    const stored = Number(localStorage.getItem('default_company'));
    if (Number.isInteger(stored) && stored > 0 && companies.some((c) => c.id === stored)) {
      return stored;
    }

    // Prefer an active company for the final fallback — a deactivated one
    // (e.g. first alphabetically) should never be presented as "current".
    return (companies.find((c) => c.status !== 0) ?? companies[0])?.id ?? null;
  });

  /** `companyID` can be a single id or an array (multi-company admins) — normalize to one id. */
  private normalizeCompanyId(companyId: unknown): number | null {
    if (Array.isArray(companyId)) {
      const first = companyId.find((id) => !!id);
      return first != null ? Number(first) : null;
    }
    return companyId != null && companyId !== '' ? Number(companyId) : null;
  }

  readonly activeCompany = computed<SwitchableCompanyView | null>(() => {
    const activeId = this.activeCompanyId();
    return this.companies().find((company) => company.id === activeId) ?? null;
  });

  /**
   * Name shown on the trigger before the list resolves.
   *
   * Bug 8724: falls back to a real label instead of `''` - with `companies()`
   * empty (still loading, or the list request failed), `activeCompany()` has
   * nothing to match against, and a blank name left the trigger as a bare,
   * unlabeled icon with no indication it was even the company switcher.
   */
  readonly activeCompanyName = computed<string>(
    () => this.activeCompany()?.company_name ?? (this.hasError() ? 'Switch company' : ''),
  );

  readonly filteredCompanies = computed<SwitchableCompanyView[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return this.companies();
    return this.companies().filter(
      (company) =>
        company.company_name?.toLowerCase().includes(term) ||
        company.short_name?.toLowerCase().includes(term),
    );
  });

  readonly isEmpty = computed<boolean>(
    () => !this.isLoading() && !this.hasError() && this.companies().length === 0,
  );

  /** Row currently highlighted by keyboard navigation; -1 when none. */
  readonly activeIndex = signal<number>(-1);

  /** Rows that can actually be moved to — deactivated entries are skipped. */
  private get navigableCompanies(): SwitchableCompanyView[] {
    return this.filteredCompanies().filter((company) => !this.isDeactivated(company));
  }

  ngOnInit(): void {
    if (this.isVisible()) this.loadCompanies();
  }

  ngOnDestroy(): void {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  /** "Dallas, Texas" — renders only the parts that exist, never a stray comma. */
  formatLocation(company: SwitchableCompany): string {
    return [company.city_name, company.state_name].filter(Boolean).join(', ');
  }

  isActive(company: SwitchableCompany): boolean {
    return company.id === this.activeCompanyId();
  }

  isDeactivated(company: SwitchableCompany): boolean {
    return company.status === 0;
  }

  initialOf(company: SwitchableCompany): string {
    return company.company_name?.charAt(0)?.toUpperCase() ?? 'C';
  }

  toggleDropdown(): void {
    if (!this.canSwitch()) return;
    const next = !this.isOpen();
    this.isOpen.set(next);
    if (next) {
      this.searchTerm.set('');
      this.activeIndex.set(-1);
      this.opened.emit();
    }
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    this.activeIndex.set(-1);
  }

  isHighlighted(company: SwitchableCompanyView): boolean {
    return this.navigableCompanies[this.activeIndex()]?.id === company.id;
  }

  /** Typing re-filters the list, so any highlight from before no longer applies. */
  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.activeIndex.set(-1);
  }

  /**
   * Arrow-key navigation over the panel.
   *
   * Bound at the panel level rather than per row so the list stays navigable
   * while focus is still in the search box.
   */
  onPanelKeydown(event: Event): void {
    if (!this.isOpen() || !(event instanceof KeyboardEvent)) return;

    const rows = this.navigableCompanies;
    if (!rows.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex.set((this.activeIndex() + 1) % rows.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex.set(this.activeIndex() <= 0 ? rows.length - 1 : this.activeIndex() - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.activeIndex.set(0);
        break;
      case 'End':
        event.preventDefault();
        this.activeIndex.set(rows.length - 1);
        break;
      case 'Enter': {
        const highlighted = rows[this.activeIndex()];
        if (highlighted) {
          event.preventDefault();
          this.selectCompany(highlighted);
        }
        break;
      }
      default:
        break;
    }
  }

  selectCompany(company: SwitchableCompanyView): void {
    // Deactivated entities are refused server-side anyway; blocking here avoids a
    // pointless round-trip and a confusing error.
    if (this.isDeactivated(company) || this.isActive(company)) return;
    this.closeDropdown();
    this.companySelected.emit({ from: this.activeCompany(), to: company });
  }

  retry(): void {
    this.loadCompanies();
  }

  private loadCompanies(): void {
    this.isLoading.set(true);
    this.hasError.set(false);

    this.commonService.empCompanyManageList().subscribe({
      next: (response: { data?: SwitchableCompany[] }) => {
        const rows = response?.data ?? [];
        this.companies.set(rows.map((row) => ({ ...row, logoUrl: null })));
        this.isLoading.set(false);
        this.hydrateLogos();
      },
      error: (err: unknown) => {
        this.logger.error('Failed to load switchable companies', err);
        this.companies.set([]);
        this.isLoading.set(false);
        this.hasError.set(true);
      },
    });
  }

  /**
   * Logos come back as S3 keys; exchange each for a blob URL we can render.
   *
   * `getFile()` already applies `encodeURIComponent` internally (see
   * `common.ts`) — encoding here too double-encodes the key (a `/` becomes
   * `%2F`, then the `%` becomes `%25`, i.e. `uploads%252F...`). The server
   * decodes once, so it looks for a file literally named `uploads%2F....enc`,
   * which never exists: every logo request 500s. With up to 6-7 companies in
   * the switcher that's several consecutive failures on the same gateway
   * circuit breaker (masters-service, `failureThreshold: 3`), which trips it
   * open and returns 503 to every other masters-service call — including
   * unrelated ones like the post-switch company-parameters fetch — for the
   * next 30 seconds. This is what looked like the backend crashing on switch.
   */
  private hydrateLogos(): void {
    const companiesWithLogos = this.companies().filter((c) => !!c.logo);
    this.hydrateLogoAt(companiesWithLogos, 0);
  }

  /**
   * Fetches one company's logo, then — only once that request has settled —
   * moves on to the next. Deliberately sequential, not `forEach`/parallel:
   *
   * A switcher panel can list 6-7 companies, and the shared masters-service
   * circuit breaker trips after just 3 consecutive failures across ANY route
   * it guards. Firing every logo request at once means that if logos are
   * failing for a reason that has nothing to do with this feature (a stale S3
   * key, a storage outage), this component alone can trip the breaker and
   * 503 unrelated calls — including the post-switch company-parameters fetch,
   * which is what made a company switch look like it crashed the backend.
   *
   * Stopping at the first failure caps this component's worst-case
   * contribution at exactly 1 failure, which no reasonable breaker threshold
   * treats as "down". The visible cost is a letter-badge fallback instead of
   * a logo for the rows after the failing one — not a functional loss.
   */
  private hydrateLogoAt(companies: SwitchableCompanyView[], index: number): void {
    if (index >= companies.length) return;
    const company = companies[index];

    this.commonService.getFile(company.logo!).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        this.objectUrls.push(url);
        this.companies.update((rows) =>
          rows.map((row) => (row.id === company.id ? { ...row, logoUrl: url } : row)),
        );
        this.hydrateLogoAt(companies, index + 1);
      },
      error: () => {
        // Stop the chain here rather than continuing to the rest of the list.
      },
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  // The shared modal/dropdown primitives do not handle Escape, so it is wired here.
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.closeDropdown();
  }
}
